import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RevenueIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { AccountRevenueSharingInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAccountRevenueSharingList,
  fetchListAccountRevenueSharingFilters,
  updateAccountRevenueSharingFilters,
  setSortData,
} from '../../../redux/slices/AccountRevenueSharing';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { disablePaymentManagementApplied } from '../../../redux/slices/PaymentManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueSharingApi from '../../../utils/api/revenue';

import RevenueManagementToggle from './RevenueManageToggle';

interface AccountRevenueSharingProperties {
  currentPage: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * User Management page function component
 */
function AccountRevenueSharing(properties: AccountRevenueSharingProperties) {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage } = properties;

  const dispatch = useAppDispatch();
  const {
    revenueList,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueSharing);

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Fetch Users list function
   */
  const fetchRevenueSharing = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAccountRevenueSharingList(data));
    }
  };
  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchRevenueSharing({ ...value, ...queryParameters, sortField, sortBy });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
    },
    {
      label: t('accountId'),
      accessorKey: 'groupBy' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('totalAmount'),
      accessorKey: 'totalRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * total power
       * @param RowData
       * @returns
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(RowData?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('totalPowerConsumed'),
      accessorKey: 'totalMeterDifference' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * total power
       * @param RowData
       * @returns
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(RowData?.totalMeterDifference, 3)}</p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('facilityOwnerRevenue'),
      accessorKey: 'totalFoRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * total power
       * @param RowData
       * @returns
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(RowData?.totalFoRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('superAdminRevenue'),
      accessorKey: 'totalAdminRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * total power
       * @param RowData
       * @returns
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(RowData?.totalAdminRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('accountId'), accessorKey: 'groupBy' },
    { label: `${t('totalAmount')} (${branding.text.currency})`, accessorKey: 'totalRevenue' },
    { label: `${t('totalPowerConsumed')} (Kwh)`, accessorKey: 'totalMeterDifference' },
    { label: t('facilityOwnerRevenue'), accessorKey: 'totalFoRevenue' },
    { label: t('superAdminRevenue'), accessorKey: 'totalAdminRevenue' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
      groupBy: 'account',
    };
    if (sortField && sortBy) {
      payload.sortField = sortField;
      payload.sortBy = sortBy;
    }
    revenueSharingApi
      .fetchRevenueSharingExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AccountRevenueSharingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row.date ? ConvertUTCtoDate(row.date) : t('na');

              case 'accountName':
                return row.accountName || t('na');

              case 'groupBy':
                return row.groupBy || t('na');

              case 'totalRevenue':
                return row.totalRevenue
                  ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'totalMeterDifference':
                return row.totalMeterDifference
                  ? ToFixedValue(Number(row.totalMeterDifference))
                  : '0.00';

              case 'totalFoRevenue':
                return row.totalFoRevenue
                  ? `${ToFixedValue(row.totalFoRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'totalAdminRevenue':
                return row.totalAdminRevenue
                  ? `${ToFixedValue(row.totalAdminRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-sharing-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-sharing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchRevenueSharing({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchRevenueSharing({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || revenueList.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchRevenueSharing({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('revenueSharing'), icon: <RevenueIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        version="v2"
        height={window.innerHeight >= 700 ? 337 : 320}
        data={revenueList}
        serialNo
        sizeChangeButtonRequired
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateAccountRevenueSharingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListAccountRevenueSharingFilters}
        rightTopContent={
          <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
        }
        ReferenceCurrentPages={handleRefresh}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default AccountRevenueSharing;
